"use client";

import { useState } from "react";
import { Button } from "@/ui/components/primitives/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/ui/components/primitives/sheet";
import { Badge } from "@/ui/components/primitives/badge";
import {
  Menu,
  RefreshCw,
  Download,
  Settings,
  Filter,
  Search,
  Bell
} from "lucide-react";

interface AdminMobileActionsProps {
  onRefresh?: () => void;
  onExport?: () => void;
  isRefreshing?: boolean;
  notificationCount?: number;
}

export function AdminMobileActions({
  onRefresh,
  onExport,
  isRefreshing = false,
  notificationCount = 0
}: AdminMobileActionsProps) {
  const [isOpen, setIsOpen] = useState(false);

  const actions = [
    {
      icon: RefreshCw,
      label: "Refresh",
      onClick: onRefresh,
      disabled: isRefreshing,
      badge: null
    },
    {
      icon: Download,
      label: "Export",
      onClick: onExport,
      badge: null
    },
    {
      icon: Filter,
      label: "Filters",
      onClick: () => {},
      badge: null
    },
    {
      icon: Search,
      label: "Search",
      onClick: () => {},
      badge: null
    },
    {
      icon: Bell,
      label: "Notifications",
      onClick: () => {},
      badge: notificationCount > 0 ? notificationCount : null
    },
    {
      icon: Settings,
      label: "Settings",
      onClick: () => {},
      badge: null
    }
  ];

  return (
    <div className="md:hidden">
      {/* Mobile Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border">
        <div className="flex items-center justify-around p-2">
          {actions.slice(0, 4).map((action, index) => (
            <Button
              key={index}
              variant="ghost"
              size="sm"
              onClick={action.onClick}
              disabled={action.disabled}
              className="flex flex-col items-center gap-1 h-auto py-2 px-3 relative"
            >
              <action.icon className="h-4 w-4" />
              <span className="text-xs">{action.label}</span>
              {action.badge && (
                <Badge
                  variant="destructive"
                  className="absolute -top-1 -right-1 h-5 w-5 p-0 text-xs flex items-center justify-center"
                >
                  {action.badge > 9 ? '9+' : action.badge}
                </Badge>
              )}
            </Button>
          ))}

          {/* More Actions Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="flex flex-col items-center gap-1 h-auto py-2 px-3"
              >
                <Menu className="h-4 w-4" />
                <span className="text-xs">More</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-[50vh]">
              <SheetHeader>
                <SheetTitle>Quick Actions</SheetTitle>
              </SheetHeader>
              <div className="grid grid-cols-2 gap-4 p-4">
                {actions.map((action, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    onClick={() => {
                      action.onClick?.();
                      setIsOpen(false);
                    }}
                    disabled={action.disabled}
                    className="flex flex-col items-center gap-2 h-auto py-4 relative"
                  >
                    <action.icon className="h-5 w-5" />
                    <span className="text-sm">{action.label}</span>
                    {action.badge && (
                      <Badge
                        variant="destructive"
                        className="absolute top-2 right-2 h-5 w-5 p-0 text-xs flex items-center justify-center"
                      >
                        {action.badge > 9 ? '9+' : action.badge}
                      </Badge>
                    )}
                  </Button>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Spacer to prevent content overlap */}
      <div className="h-16 md:hidden"></div>
    </div>
  );
}